import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  TextField,
  Button,
  IconButton,
  Divider,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery,
  FormControl,
  Autocomplete
} from '@mui/material';
import { motion } from 'framer-motion';
import { useLanguage } from './LanguageProvider';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SendIcon from '@mui/icons-material/Send';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import InstagramIcon from '@mui/icons-material/Instagram';
import WebIcon from '@mui/icons-material/Language';
import PersonIcon from '@mui/icons-material/Person';

export const Contactus = () => {
  const { currentLang, direction } = useLanguage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+971',
    phone: '',
    subject: '',
    message: ''
  });
  
  const [formErrors, setFormErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    }
  };

  const floatingAnimation = {
    y: [0, -15, 0],
    transition: {
      duration: 4,
      repeat: Infinity,
      ease: "easeInOut"
    }
  };

  // Country codes data with search
  const countryCodes = [
    { code: '+971', country: 'UAE', flag: '🇦🇪', name: 'الإمارات' },
    { code: '+966', country: 'Saudi Arabia', flag: '🇸🇦', name: 'السعودية' },
    { code: '+974', country: 'Qatar', flag: '🇶🇦', name: 'قطر' },
    { code: '+973', country: 'Bahrain', flag: '🇧🇭', name: 'البحرين' },
    { code: '+968', country: 'Oman', flag: '🇴🇲', name: 'عمان' },
    { code: '+965', country: 'Kuwait', flag: '🇰🇼', name: 'الكويت' },
    { code: '+20', country: 'Egypt', flag: '🇪🇬', name: 'مصر' },
    { code: '+962', country: 'Jordan', flag: '🇯🇴', name: 'الأردن' },
    { code: '+961', country: 'Lebanon', flag: '🇱🇧', name: 'لبنان' },
    { code: '+963', country: 'Syria', flag: '🇸🇾', name: 'سوريا' },
    { code: '+964', country: 'Iraq', flag: '🇮🇶', name: 'العراق' },
    { code: '+967', country: 'Yemen', flag: '🇾🇪', name: 'اليمن' },
    { code: '+218', country: 'Libya', flag: '🇱🇾', name: 'ليبيا' },
    { code: '+216', country: 'Tunisia', flag: '🇹🇳', name: 'تونس' },
    { code: '+213', country: 'Algeria', flag: '🇩🇿', name: 'الجزائر' },
    { code: '+212', country: 'Morocco', flag: '🇲🇦', name: 'المغرب' },
    { code: '+249', country: 'Sudan', flag: '🇸🇩', name: 'السودان' },
    { code: '+1', country: 'USA/Canada', flag: '🇺🇸', name: 'أمريكا/كندا' },
    { code: '+44', country: 'UK', flag: '🇬🇧', name: 'بريطانيا' },
    { code: '+33', country: 'France', flag: '🇫🇷', name: 'فرنسا' },
    { code: '+49', country: 'Germany', flag: '🇩🇪', name: 'ألمانيا' },
    { code: '+39', country: 'Italy', flag: '🇮🇹', name: 'إيطاليا' },
    { code: '+34', country: 'Spain', flag: '🇪🇸', name: 'إسبانيا' },
    { code: '+91', country: 'India', flag: '🇮🇳', name: 'الهند' },
    { code: '+92', country: 'Pakistan', flag: '🇵🇰', name: 'باكستان' },
    { code: '+880', country: 'Bangladesh', flag: '🇧🇩', name: 'بنغلاديش' },
    { code: '+94', country: 'Sri Lanka', flag: '🇱🇰', name: 'سريلانكا' },
    { code: '+60', country: 'Malaysia', flag: '🇲🇾', name: 'ماليزيا' },
    { code: '+65', country: 'Singapore', flag: '🇸🇬', name: 'سنغافورة' },
    { code: '+86', country: 'China', flag: '🇨🇳', name: 'الصين' },
    { code: '+81', country: 'Japan', flag: '🇯🇵', name: 'اليابان' },
    { code: '+82', country: 'South Korea', flag: '🇰🇷', name: 'كوريا الجنوبية' }
  ];

  // Updated contact information with real data
  const contactInfo = {
    en: [
      {
        icon: PersonIcon,
        title: 'CEO & Legal Consultant',
        details: ['Hatem Elsegaei'],
        color: '#d4af37'
      },
      {
        icon: PhoneIcon,
        title: 'Phone',
        details: ['+971 50 333 0841'],
        color: '#d4af37'
      },
      {
        icon: EmailIcon,
        title: 'Email',
        details: ['info@almaryahlc.com', 'almaryahlconsaltancy@gmail.com'],
        color: '#d4af37'
      },
      {
        icon: LocationOnIcon,
        title: 'Office',
        details: ['Twin Towers, Fujairah, UAE', 'P.O. Box 4422'],
        color: '#d4af37'
      }
    ],
    ar: [
      {
        icon: PersonIcon,
        title: 'الرئيس التنفيذي والمستشار القانوني',
        details: ['حاتم الصغيري'],
        color: '#d4af37'
      },
      {
        icon: PhoneIcon,
        title: 'الهاتف',
        details: ['+971 50 333 0841'],
        color: '#d4af37'
      },
      {
        icon: EmailIcon,
        title: 'البريد الإلكتروني',
        details: ['info@almaryahlc.com', 'almaryahlconsaltancy@gmail.com'],
        color: '#d4af37'
      },
      {
        icon: LocationOnIcon,
        title: 'المكتب',
        details: ['برج التوأم، الفجيرة، الإمارات', 'ص.ب ٤٤٢٢'],
        color: '#d4af37'
      }
    ]
  };

  // Social media links with updated domains
  const socialMedia = [
    { icon: WhatsAppIcon, link: 'https://wa.me/971503330841', color: '#25D366', name: 'WhatsApp' },
    { icon: WebIcon, link: 'https://almaryahlc.com', color: '#d4af37', name: 'Website' },
    { icon: LinkedInIcon, link: 'https://linkedin.com/company/almaryah-legal-consultancy', color: '#0A66C2', name: 'LinkedIn' }
  ];

  // Working hours
  const workingHours = {
    en: {
      title: 'Working Hours',
      details: ['Monday - Friday: 9:00 AM - 6:00 PM', 'Saturday: 10:00 AM - 2:00 PM', 'Sunday: Closed']
    },
    ar: {
      title: 'ساعات العمل',
      details: ['الإثنين - الجمعة: ٩:٠٠ ص - ٦:٠٠ م', 'السبت: ١٠:٠٠ ص - ٢:٠٠ م', 'الأحد: مغلق']
    }
  };

  const currentContactInfo = contactInfo[currentLang];
  const currentWorkingHours = workingHours[currentLang];

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phone') {
      const numbersOnly = value.replace(/[^0-9]/g, '');
      setFormData(prev => ({ ...prev, [name]: numbersOnly }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = currentLang === 'ar' ? 'الاسم مطلوب' : 'Name is required';
    }
    
    if (!formData.email.trim()) {
      errors.email = currentLang === 'ar' ? 'البريد الإلكتروني مطلوب' : 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = currentLang === 'ar' ? 'البريد الإلكتروني غير صحيح' : 'Email is invalid';
    }
    
    if (!formData.phone.trim()) {
      errors.phone = currentLang === 'ar' ? 'رقم الهاتف مطلوب' : 'Phone number is required';
    } else if (formData.phone.length < 7) {
      errors.phone = currentLang === 'ar' ? 'رقم الهاتف قصير جداً' : 'Phone number is too short';
    } else if (formData.phone.length > 15) {
      errors.phone = currentLang === 'ar' ? 'رقم الهاتف طويل جداً' : 'Phone number is too long';
    }
    
    if (!formData.subject.trim()) {
      errors.subject = currentLang === 'ar' ? 'الموضوع مطلوب' : 'Subject is required';
    }
    
    if (!formData.message.trim()) {
      errors.message = currentLang === 'ar' ? 'الرسالة مطلوبة' : 'Message is required';
    } else if (formData.message.trim().length < 10) {
      errors.message = currentLang === 'ar' ? 'الرسالة قصيرة جداً (على الأقل 10 أحرف)' : 'Message is too short (min 10 characters)';
    }
    
    return errors;
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setSnackbar({
        open: true,
        message: currentLang === 'ar' ? 'يرجى تصحيح الأخطاء في النموذج' : 'Please correct the errors in the form',
        severity: 'error'
      });
      return;
    }
    
    setIsSubmitting(true);
    
    const submissionData = {
      name: formData.name,
      email: formData.email,
      phonecode: formData.countryCode,
      phonenumber: formData.phone,
      subject: formData.subject,
      message: formData.message
    };
    
    try {
      console.log('Sending data:', submissionData);
      
      const response = await fetch('https://almariaapi.onrender.com/api/v1/clients', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData)
      });

      const data = await response.json();
      console.log('Response:', data);

      if (response.ok) {
        setSnackbar({
          open: true,
          message: currentLang === 'ar' 
            ? '✅ تم إرسال رسالتك بنجاح! سنتواصل معك قريباً' 
            : '✅ Your message has been sent successfully! We\'ll contact you soon.',
          severity: 'success'
        });
        
        setFormData({
          name: '',
          email: '',
          countryCode: '+971',
          phone: '',
          subject: '',
          message: ''
        });
      } else {
        throw new Error(data.message || 'Something went wrong');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setSnackbar({
        open: true,
        message: currentLang === 'ar' 
          ? '❌ حدث خطأ أثناء الإرسال. حاول مرة أخرى' 
          : '❌ An error occurred. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle snackbar close
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Updated Google Maps link for Fujairah - Twin Towers
  const googleMapsEmbedUrl = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3619.423456789012!2d56.327156!3d25.128456!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ef7f5d5c5b5c5b5%3A0x5c5b5c5b5c5b5c5b!2sTwin%20Towers%2C%20Fujairah!5e0!3m2!1sen!2sae!4v1234567890123!5m2!1sen!2sae";

  return (
    <Box
      id="contact"
      sx={{
        py: { xs: 8, md: 12 },
        background: 'linear-gradient(135deg, #f5f7fa 0%, #e9eef5 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Animated Background Elements */}
      <motion.div
        animate={{
          scale: [1, 1.2, 1],
          rotate: [0, 45, 0],
          opacity: [0.1, 0.15, 0.1]
        }}
        transition={{
          duration: 15,
          repeat: Infinity,
          ease: "linear"
        }}
        style={{
          position: 'absolute',
          top: '5%',
          right: '5%',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(212,175,55,0.1) 0%, rgba(212,175,55,0) 70%)',
          zIndex: 0,
          pointerEvents: 'none'
        }}
      />

      <motion.div
        animate={floatingAnimation}
        style={{
          position: 'absolute',
          bottom: '10%',
          left: '5%',
          width: '200px',
          height: '200px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(212,175,55,0.1) 0%, rgba(212,175,55,0) 70%)',
          zIndex: 0,
          pointerEvents: 'none'
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Section Title */}
          <motion.div variants={itemVariants}>
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography
                variant="h3"
                component="h2"
                sx={{
                  fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                  fontWeight: 'bold',
                  color: '#1a1a2e',
                  mb: 2,
                  fontSize: { xs: '2rem', md: '2.5rem' }
                }}
              >
                {currentLang === 'ar' ? 'اتصل بنا' : 'Contact Us'}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                  color: '#d4af37',
                  fontWeight: 500,
                  mb: 3,
                  maxWidth: '600px',
                  mx: 'auto'
                }}
              >
                {currentLang === 'ar'
                  ? 'نحن هنا للإجابة على استفساراتكم'
                  : "We're here to answer your inquiries"}
              </Typography>
              <Divider sx={{
                width: '80px',
                margin: '0 auto',
                height: '4px',
                background: 'linear-gradient(90deg, #d4af37, #b8942f)',
                borderRadius: '2px'
              }} />
            </Box>
          </motion.div>

          {/* Contact Info Cards */}
          <Grid container spacing={3} sx={{ mb: 6 }}>
            {currentContactInfo.map((info, index) => {
              const Icon = info.icon;
              return (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <motion.div
                    variants={itemVariants}
                    whileHover={{
                      y: -10,
                      scale: 1.02,
                      transition: { type: "spring", stiffness: 300 }
                    }}
                    style={{ height: '100%' }}
                  >
                    <Paper
                      elevation={3}
                      sx={{
                        p: 3,
                        height: '100%',
                        textAlign: 'center',
                        background: 'white',
                        borderRadius: '20px',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: `0 20px 40px ${info.color}20`
                        }
                      }}
                    >
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: '50%',
                          bgcolor: `${info.color}10`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto 16px',
                          border: `2px solid ${info.color}`
                        }}
                      >
                        <Icon sx={{ color: info.color, fontSize: 30 }} />
                      </Box>
                      
                      <Typography
                        variant="h6"
                        sx={{
                          fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                          fontWeight: 'bold',
                          color: '#1a1a2e',
                          mb: 2
                        }}
                      >
                        {info.title}
                      </Typography>
                      
                      {info.details.map((detail, idx) => (
                        <Typography
                          key={idx}
                          variant="body2"
                          sx={{
                            fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                            color: '#666',
                            lineHeight: 1.8
                          }}
                        >
                          {detail}
                        </Typography>
                      ))}
                    </Paper>
                  </motion.div>
                </Grid>
              );
            })}
          </Grid>

          {/* Working Hours Card */}
          <Grid container spacing={3} sx={{ mb: 6 }}>
            <Grid item xs={12} sm={6} md={4} sx={{ mx: 'auto' }}>
              <motion.div variants={itemVariants}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 3,
                    textAlign: 'center',
                    background: 'white',
                    borderRadius: '20px',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      bgcolor: '#d4af3710',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 16px',
                      border: '2px solid #d4af37'
                    }}
                  >
                    <AccessTimeIcon sx={{ color: '#d4af37', fontSize: 30 }} />
                  </Box>
                  
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                      fontWeight: 'bold',
                      color: '#1a1a2e',
                      mb: 2
                    }}
                  >
                    {currentWorkingHours.title}
                  </Typography>
                  
                  {currentWorkingHours.details.map((detail, idx) => (
                    <Typography
                      key={idx}
                      variant="body2"
                      sx={{
                        fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                        color: '#666',
                        lineHeight: 1.8
                      }}
                    >
                      {detail}
                    </Typography>
                  ))}
                </Paper>
              </motion.div>
            </Grid>
          </Grid>

          {/* Contact Form and Map */}
          <Grid container spacing={4}>
            {/* Contact Form */}
            <Grid item xs={12} md={7}>
              <motion.div variants={itemVariants} style={{ height: '100%' }}>
                <Paper
                  elevation={3}
                  sx={{
                    p: { xs: 3, md: 4 },
                    background: 'white',
                    borderRadius: '20px',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                      fontWeight: 'bold',
                      color: '#1a1a2e',
                      mb: 3
                    }}
                  >
                    {currentLang === 'ar' ? 'أرسل لنا رسالة' : 'Send us a Message'}
                  </Typography>

                  <form onSubmit={handleSubmit} style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Grid container spacing={2} sx={{ flex: 1 }}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label={currentLang === 'ar' ? 'الاسم' : 'Name'}
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          error={!!formErrors.name}
                          helperText={formErrors.name}
                          disabled={isSubmitting}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': {
                                borderColor: '#d4af37',
                              },
                            },
                          }}
                        />
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label={currentLang === 'ar' ? 'البريد الإلكتروني' : 'Email'}
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          error={!!formErrors.email}
                          helperText={formErrors.email}
                          disabled={isSubmitting}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': {
                                borderColor: '#d4af37',
                              },
                            },
                          }}
                        />
                      </Grid>
                      
                      {/* Phone Number Field with Search */}
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                          <FormControl sx={{ width: { xs: '100%', sm: '35%' } }}>
                            <Autocomplete
                              value={countryCodes.find(c => c.code === formData.countryCode) || countryCodes[0]}
                              onChange={(event, newValue) => {
                                if (newValue) {
                                  setFormData(prev => ({ ...prev, countryCode: newValue.code }));
                                }
                              }}
                              options={countryCodes}
                              getOptionLabel={(option) => {
                                if (currentLang === 'ar') {
                                  return `${option.flag} ${option.code} (${option.name})`;
                                }
                                return `${option.flag} ${option.code} (${option.country})`;
                              }}
                              renderOption={(props, option) => (
                                <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <span>{option.flag}</span>
                                  <span style={{ fontWeight: 'bold' }}>{option.code}</span>
                                  <span style={{ color: '#666', fontSize: '0.9rem' }}>
                                    {currentLang === 'ar' ? option.name : option.country}
                                  </span>
                                </Box>
                              )}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label={currentLang === 'ar' ? 'الدولة والكود' : 'Country & Code'}
                                  placeholder={currentLang === 'ar' ? 'ابحث عن دولة...' : 'Search country...'}
                                  disabled={isSubmitting}
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      '&:hover fieldset': {
                                        borderColor: '#d4af37',
                                      },
                                    },
                                  }}
                                />
                              )}
                              isOptionEqualToValue={(option, value) => option.code === value.code}
                              disableClearable
                            />
                          </FormControl>

                          <TextField
                            fullWidth
                            label={currentLang === 'ar' ? 'رقم الهاتف' : 'Phone Number'}
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            error={!!formErrors.phone}
                            helperText={formErrors.phone}
                            disabled={isSubmitting}
                            inputProps={{
                              inputMode: 'numeric',
                              pattern: '[0-9]*'
                            }}
                            sx={{
                              width: { xs: '100%', sm: '65%' },
                              '& .MuiOutlinedInput-root': {
                                '&:hover fieldset': {
                                  borderColor: '#d4af37',
                                },
                              },
                            }}
                          />
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label={currentLang === 'ar' ? 'الموضوع' : 'Subject'}
                          name="subject"
                          value={formData.subject}
                          onChange={handleInputChange}
                          error={!!formErrors.subject}
                          helperText={formErrors.subject}
                          disabled={isSubmitting}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': {
                                borderColor: '#d4af37',
                              },
                            },
                          }}
                        />
                      </Grid>
                      
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label={currentLang === 'ar' ? 'الرسالة' : 'Message'}
                          name="message"
                          multiline
                          rows={4}
                          value={formData.message}
                          onChange={handleInputChange}
                          error={!!formErrors.message}
                          helperText={formErrors.message}
                          disabled={isSubmitting}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': {
                                borderColor: '#d4af37',
                              },
                            },
                          }}
                        />
                      </Grid>
                      
                      <Grid item xs={12} sx={{ mt: 'auto' }}>
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          fullWidth
                          endIcon={<SendIcon />}
                          disabled={isSubmitting}
                          sx={{
                            background: 'linear-gradient(45deg, #d4af37 30%, #b8942f 90%)',
                            color: '#0b0c1e',
                            py: 1.5,
                            fontSize: '1.1rem',
                            fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                            fontWeight: 600,
                            borderRadius: '10px',
                            '&:hover': {
                              background: 'linear-gradient(45deg, #b8942f 30%, #d4af37 90%)',
                            },
                            '&.Mui-disabled': {
                              background: '#ccc'
                            }
                          }}
                        >
                          {isSubmitting 
                            ? (currentLang === 'ar' ? 'جاري الإرسال...' : 'Sending...') 
                            : (currentLang === 'ar' ? 'إرسال الرسالة' : 'Send Message')}
                        </Button>
                      </Grid>
                    </Grid>
                  </form>
                </Paper>
              </motion.div>
            </Grid>

            {/* Map and Social Media */}
            <Grid item xs={12} md={5}>
              <motion.div variants={itemVariants} style={{ height: '100%' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 3 }}>
                  {/* Map */}
                  <Paper
                    elevation={3}
                    sx={{
                      p: 2,
                      background: 'white',
                      borderRadius: '20px',
                      overflow: 'hidden',
                      flex: 6
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                        fontWeight: 'bold',
                        color: '#1a1a2e',
                        mb: 2,
                        textAlign: 'center'
                      }}
                    >
                      {currentLang === 'ar' ? 'موقعنا' : 'Our Location'}
                    </Typography>
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        minHeight: { xs: 250, md: 300 },
                        borderRadius: '15px',
                        overflow: 'hidden',
                        position: 'relative'
                      }}
                    >
                      <iframe
                        src={googleMapsEmbedUrl}
                        width="100%"
                        height="100%"
                        style={{ border: 0 }}
                        allowFullScreen=""
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        title="Almaryah Legal Consultancy - Twin Towers, Fujairah"
                      />
                    </Box>
                  </Paper>

                  {/* Social Media */}
                  <Paper
                    elevation={3}
                    sx={{
                      p: 3,
                      background: 'white',
                      borderRadius: '20px',
                      flex: 4,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                        fontWeight: 'bold',
                        color: '#1a1a2e',
                        mb: 2,
                        textAlign: 'center'
                      }}
                    >
                      {currentLang === 'ar' ? 'تابعنا على' : 'Follow Us On'}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                      {socialMedia.map((social, index) => {
                        const Icon = social.icon;
                        return (
                          <motion.div
                            key={index}
                            whileHover={{
                              scale: 1.1,
                              y: -5,
                              transition: { type: "spring", stiffness: 300 }
                            }}
                            whileTap={{ scale: 0.95 }}
                          >
                            <IconButton
                              component="a"
                              href={social.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                bgcolor: `${social.color}10`,
                                color: social.color,
                                width: 48,
                                height: 48,
                                border: `2px solid ${social.color}`,
                                '&:hover': {
                                  bgcolor: social.color,
                                  color: 'white'
                                }
                              }}
                            >
                              <Icon />
                            </IconButton>
                          </motion.div>
                        );
                      })}
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ textAlign: 'center' }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                          color: '#666',
                          mb: 1
                        }}
                      >
                        {currentLang === 'ar' ? 'أو اتصل بنا مباشرة على' : 'Or call us directly at'}
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                          fontWeight: 'bold',
                          color: '#d4af37'
                        }}
                      >
                        +971 50 333 0841
                      </Typography>
                    </Box>
                  </Paper>
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </motion.div>
      </Container>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{
            width: '100%',
            fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
            '& .MuiAlert-icon': {
              alignItems: 'center'
            }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Contactus;